import React, { memo, useRef, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { FlexCenter } from "styles/global-style";
import Button from "components/Button";
import ButtonPlus from "components/ButtonPlus";

// Helper function to calculate week number (moved outside component to avoid hooks order issues)
const getWeekNumber = (date) => {
  // ISO week numbering: Week 1 is the week containing January 4th
  // Get the first Thursday of the year
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const firstThursday = new Date(firstDayOfYear);
  
  // Find the first Thursday (day 4, where 0 = Sunday)
  const dayOfWeek = firstDayOfYear.getDay();
  const daysToThursday = dayOfWeek <= 4 ? 4 - dayOfWeek : 11 - dayOfWeek;
  firstThursday.setDate(firstDayOfYear.getDate() + daysToThursday);
  
  // Calculate weeks since the first Thursday
  const timeDiff = date.getTime() - firstThursday.getTime();
  const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const weekNumber = Math.floor(daysDiff / 7) + 1;
  
  return weekNumber;
};

export default memo(function Date({ date, weekEnd, isWeekMode, showButton, size, addGoalCb, addPersonCb, dateClickCb }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });

  const containerRef = useRef();
  const anchorRef = useRef();

  const onClickHandler = useCallback(
    (e) => {
      e.stopPropagation();
      dateClickCb(!menuOpen);

      const clickListener = (e) => {
        if (!containerRef.current?.contains(e.target)) {
          window.removeEventListener("mouseup", clickListener);
          dateClickCb(false);
          setMenuOpen(false);
        }
      };

      if (!menuOpen) {
        // Anchor the portal popup just below the plus button (fixed
        // coordinates, so the scroller's overflow can't clip it).
        const rect = anchorRef.current?.getBoundingClientRect();
        if (rect) setMenuPos({ x: rect.left + rect.width / 2, y: rect.bottom + 5 });
        window.addEventListener("mouseup", clickListener);
      } else {
        window.removeEventListener("mouseup", clickListener);
      }
      setMenuOpen(!menuOpen);
    },
    [dateClickCb, setMenuOpen, menuOpen]
  );

  // The popup is fixed-positioned; close it if anything scrolls so it doesn't
  // drift away from its anchor.
  useEffect(() => {
    if (!menuOpen) return;
    const onScroll = () => {
      dateClickCb(false);
      setMenuOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [menuOpen, dateClickCb]);

  const addGoal = useCallback(() => {
    addGoalCb(date);
    setMenuOpen(false);
  }, [date, addGoalCb, setMenuOpen]);

  const addPerson = useCallback(() => {
    addPersonCb(date);
    setMenuOpen(false);
  }, [date, addPersonCb, setMenuOpen]);


  return (
    <>
      <FlexCenter ref={anchorRef} style={{ width: isWeekMode ? size * 2 : size }}>
        {showButton ? (
          <div style={{ marginTop: -3}}>
            <ButtonPlus size={size} onClick={onClickHandler} />
          </div>
        ) : (
          <div style={{ 
            fontSize: isWeekMode ? "0.5rem" : "0.6rem", 
            textAlign: "center", 
            opacity: 0.4,
          }}>
            {isWeekMode && weekEnd ? `w${getWeekNumber(date)}` : date.getUTCDate()}
          </div>
        )}
      </FlexCenter>

      {menuOpen &&
        createPortal(
        <div
          ref={containerRef}
          style={{
            position: "fixed",
            top: menuPos.y,
            left: menuPos.x,
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            minWidth: 100,
            zIndex: 10000,
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            borderRadius: 8,
            padding: "7px 7px",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", rowGap: 4 }}>
            <Button text="Add goal" callback={addGoal} lightmode noborder />
            <Button text="Person joins" callback={addPerson} lightmode noborder />
          </div>
          <div
            style={{
              position: "absolute",
              top: -10,
              left: "50%",
              transform: "translateX(-50%)",
              height: 0,
              width: 0,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderBottom: "10px solid white",
            }}
          />
        </div>,
        document.body
      )}
    </>
  );
});
